import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';

import moment from 'moment';
import { CircleArrowRightNextIcon, QuestionCircleNextIcon } from '@glu/icons-react';
import { UserLink, IconLink } from '@glu/nav-react';
import { clearGrids } from 'logic/store/grids/actions';
import memoize from 'lodash/memoize';

// Components
import useLocale from 'components/useLocale';
import { useAppConfig } from 'components/appConfig/AppConfig';
import DynamicLink2 from 'components/dynamicLink2/DynamicLink2';

// API/Services
import fetchMenuLinks from 'api/menu/service';
import { UserShape } from 'api/user/props';
import { logout, logoutAll } from 'logic/store/authentication/actions';

// Utils
import { getUserName } from 'logic/store/authentication/selectors';
import { MENU_LINK_TYPES, DATE_TIME_FORMAT } from 'constants.js';

// Relative
import LinkText from './LinkText.tsx';
import SideNav from './SideNav';
import useStyles from './SideNav.styles';

const SideNavContainer = ({ clearGrids, logout, logoutAll, user, userName }) => {
  const history = useHistory();
  const locale = useLocale();
  const { menuHighlight } = useStyles();
  const { isNative } = useAppConfig();
  const [navLinks, setNavLinks] = useState([]);
  const [modal, setModal] = useState({
    show: false,
    link: null
  });

  const formatName = memoize((name) =>
    name ? `${name.replaceAll(' ', '-').toLowerCase()}-link` : 'side-nav'
  );

  const buildLink = ({ highlight, link, name, type }) => {
    const linkTextProps = {
      key: name,
      name,
      className: 'linkText',
      highlight
    };
    return {
      onClick: (evt) => {
        evt.preventDefault();
        if (type === MENU_LINK_TYPES.DYNAMIC_LINK_NO_SPEED_BUMP) {
          window.open(link);
        } else if (type === MENU_LINK_TYPES.DYNAMIC_LINK) {
          setModal({ show: true, link });
        } else {
          history.push({
            pathname: type === MENU_LINK_TYPES.SSO ? `/sso-login/${link}` : link,
            returnURL: window.location.pathname,
            state: {
              clearTabs: true
            }
          });
        }
      },
      // eslint-disable-next-line react/jsx-props-no-spreading
      children: [<LinkText {...linkTextProps} />],
      href: link,
      'data-qa': formatName(name)
    };
  };

  const buildWithSubLinks = ({ highlight, name, options }) => {
    const linkTextProps = {
      key: name,
      name,
      className: { menuHighlight },
      highlight
    };
    return {
      text: '',
      onClick: (evt) => evt.preventDefault(),
      // eslint-disable-next-line react/jsx-props-no-spreading
      children: [<LinkText {...linkTextProps} />],
      navLinks: options.map(buildLink),
      dataQa: formatName(name)
    };
  };

  const closeModal = () => {
    setModal({ ...modal, show: false });
  };

  const lastLoginInfo = user?.lastLoginTime
    ? moment(user.lastLoginTime).format(DATE_TIME_FORMAT)
    : locale['header.nouserhist'];

  const userMenuItems = [
    {
      children: (
        <span className="custom-link">
          <UserLink name={userName} lastLogin={lastLoginInfo} />
        </span>
      ),
      divider: true,
      dataQa: 'user-link'
    },
    !isNative && {
      children: (
        <IconLink className="custom-link" Icon={QuestionCircleNextIcon}>
          {locale['header.help']}
        </IconLink>
      ),
      href: '/#help',
      onClick: (evt) => {
        evt.preventDefault();
        history.push('/help-center');
      },
      'data-qa': 'help-link'
    },
    {
      children: (
        <IconLink className="custom-link" Icon={CircleArrowRightNextIcon}>
          {locale['header.logoff']}
        </IconLink>
      ),
      onClick: (evt) => {
        evt.preventDefault();
        history.replace(history.pathname, {});
        clearGrids();
        return user.bankUser ? logoutAll({ history }) : logout({ history });
      },
      'data-qa': 'logout-link',
      href: '/#logout'
    }
  ].filter(Boolean);

  const getUserLinks = () => {
    if (isNative) {
      return userMenuItems;
    }

    userMenuItems[0].navLinks = [
      {
        href: '/change-password',
        children: locale['function.ChangePwd'],
        onClick: (evt) => {
          evt.preventDefault();
          history.push('/change-password');
        },
        'data-qa': 'change-passwd-link'
      },
      {
        href: '/security-questions',
        children: locale['menuitem.SETUP.ManageSecQues'],
        onClick: (evt) => {
          evt.preventDefault();
          history.push({
            pathname: '/security-questions',
            state: {
              previous: history.location.pathname
            }
          });
        },
        'data-qa': 'sec-questions-link'
      },
      {
        href: '/alerts',
        children: locale['common.alerts'],
        onClick: (evt) => {
          evt.preventDefault();
          history.push('/alerts');
        },
        'data-qa': 'alerts-link'
      },
      {
        href: '/profile-management',
        children: locale['function.MySettings'],
        onClick: (evt) => {
          evt.preventDefault();
          history.push('/profile-management');
        },
        'data-qa': 'profile-management-link'
      }
    ];
    return userMenuItems;
  };

  useEffect(() => {
    const subscription = fetchMenuLinks().subscribe((links) => {
      const navLinks = links.map((link) =>
        link.type !== null ? buildLink(link) : buildWithSubLinks(link)
      );
      const sideNavLinks = [...navLinks, ...getUserLinks()];
      setNavLinks(sideNavLinks);
    });

    return () => subscription?.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SideNav navLinks={navLinks} />
      <DynamicLink2 show={modal.show} link={modal.link} close={closeModal} />
    </>
  );
};

SideNavContainer.propTypes = {
  user: UserShape.isRequired,
  clearGrids: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  logoutAll: PropTypes.func.isRequired,
  userName: PropTypes.string.isRequired
};

/* istanbul ignore next */
function mapStateToProps(state) {
  return {
    user: state.auth.user,
    userName: getUserName(state)
  };
}

const mapDispatchToProps = (dispatch) => ({
  logout: (...params) => dispatch(logout(...params)),
  logoutAll: (...params) => dispatch(logoutAll(...params)),
  clearGrids: () => clearGrids(dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(SideNavContainer);
